import { Box, Link } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { Components } from "react-markdown";
import ChakraUIRenderer from "../../shared-components/chakra-ui-renderer";
import { getDocIdExtension } from "./document-utils";

/**
 * Component for resource links that opens the PDF viewer modal
 */
function ResourceLink({
  resource,
  resourceId,
  children,
  onPdfView,
}: {
  resource: unknown;
  resourceId: string;
  children: React.ReactNode;
  onPdfView?: (docId: string) => void;
}) {
  const resourceObj = resource as Record<string, unknown>;

  function handleClick() {
    const docId = getDocIdExtension(resource);
    if (docId && onPdfView) {
      onPdfView(docId);
    }
  }

  const docId = getDocIdExtension(resource);
  const hasDocId = !!docId;

  return (
    <Box
      as="button"
      display="inline"
      color={hasDocId ? "purple.500" : "fg.placeholder"}
      textDecoration="underline"
      fontWeight="medium"
      cursor={hasDocId ? "pointer" : "not-allowed"}
      bg="transparent"
      border="none"
      p={0}
      _hover={
        hasDocId
          ? {
              color: "purple.600",
              bg: "purple.50",
            }
          : undefined
      }
      _dark={
        hasDocId
          ? {
              color: "purple.300",
              _hover: {
                color: "purple.200",
                bg: "purple.900",
              },
            }
          : { color: "fg.placeholder" }
      }
      title={
        hasDocId
          ? `${resourceObj.resourceType as string}: ${resourceId} - Click to view PDF`
          : `${resourceObj.resourceType as string}: ${resourceId} - No document available`
      }
      onClick={hasDocId ? handleClick : undefined}
    >
      [{children}]
    </Box>
  );
}

/**
 * Creates a custom renderer with FHIR bundle context for enhanced link handling.
 * Used for rendering markdown content with clickable FHIR resource references.
 */
export function createCustomRenderer(
  bundle: FhirBundleSdk | undefined,
  onPdfView?: (docId: string) => void
): Components {
  const baseRenderer = ChakraUIRenderer();

  // Custom link component that has access to bundle
  const contextAwareLink: Components["a"] = props => {
    const { children, href } = props;

    if (!href || !bundle) {
      // No href or bundle, render as plain link
      return (
        <Link
          href={href}
          color="status.info"
          textDecoration="underline"
          _hover={{
            color: "blue.600",
          }}
          _dark={{
            color: "blue.300",
            _hover: {
              color: "blue.200",
            },
          }}
        >
          {children}
        </Link>
      );
    }

    // Check if this is a UUID or FHIR reference (Patient/uuid, Observation/uuid, or just uuid)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const fhirReferenceRegex =
      /^([A-Z][a-zA-Z]+)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

    let resourceId: string | undefined;

    // Check if it's a FHIR reference format (ResourceType/uuid)
    const fhirMatch = href.match(fhirReferenceRegex);
    if (fhirMatch) {
      resourceId = fhirMatch[2];
    } else if (uuidRegex.test(href)) {
      // Just a UUID, we'll search for it across all resources
      resourceId = href;
    }

    if (resourceId) {
      // Find resource in bundle by searching through entries
      const resource = bundle.getResourceById(resourceId);

      if (!resource) {
        // Resource not found, render as disabled link
        return (
          <Link
            color="fg.placeholder"
            textDecoration="underline"
            fontWeight="medium"
            cursor="not-allowed"
            title={`Data not found: ${resourceId}`}
          >
            [{children}]
          </Link>
        );
      }

      // Use modal for all resources
      return (
        <ResourceLink resource={resource} resourceId={resourceId} onPdfView={onPdfView}>
          {children}
        </ResourceLink>
      );
    }

    // Fall back to default link renderer for non-FHIR links
    return (
      <Link
        href={href}
        color="status.info"
        textDecoration="underline"
        _hover={{
          color: "blue.600",
        }}
        _dark={{
          color: "blue.300",
          _hover: {
            color: "blue.200",
          },
        }}
      >
        {children}
      </Link>
    );
  };

  return {
    ...baseRenderer,
    a: contextAwareLink,
  };
}
